import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { playerAPI } from '../services/api';
import './RequestReview.css';

function RequestReview() {
  const { tournamentId } = useParams();
  const navigate = useNavigate();
  
  const [players, setPlayers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // all, needs-review, processed
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadPlayers();
  }, [tournamentId]);

  const loadPlayers = async () => {
    try {
      setIsLoading(true);
      const response = await playerAPI.getAll(tournamentId);
      setPlayers(response.data);
    } catch (err) {
      console.error('Error loading players:', err);
      setError('Failed to load players. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredPlayers = players.filter(player => {
    // Filter by review status
    if (filter === 'needs-review' && !player.needsReview) return false;
    if (filter === 'processed' && !player.llmProcessed) return false;
    
    // Filter by search term
    if (searchTerm && !player.name.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    
    return true;
  });

  const updatePlayerRequests = async (playerId, updates) => {
    try {
      setSaving(true);
      await playerAPI.update(playerId, updates);
      
      // Update local state
      setPlayers(prev => prev.map(p => 
        p._id === playerId ? { ...p, ...updates } : p
      ));
      
      if (selectedPlayer && selectedPlayer._id === playerId) {
        setSelectedPlayer(prev => ({ ...prev, ...updates }));
      }
    } catch (err) {
      console.error('Error updating player:', err);
      setError('Failed to update player requests.');
    } finally {
      setSaving(false);
    }
  };

  const approveAllRequests = async (playerId) => {
    await updatePlayerRequests(playerId, {
      needsReview: false,
      reviewedAt: new Date().toISOString()
    });
  };

  const markAsNeedsReview = async (playerId) => {
    await updatePlayerRequests(playerId, {
      needsReview: true
    });
  };

  const getRequestTypeIcon = (type) => {
    const icons = {
      // Time request icons
      early: '🌅',
      late: '🌙',
      specific: '🕐',
      range: '📅',
      avoid: '❌',
      before: '⬆️',
      after: '⬇️',
      
      // Partner request icons
      specific: '👤',
      group: '👥',
      team: '🏢',
      
      // Team request icons
      corporate: '🏢',
      family: '👨‍👩‍👧‍👦',
      club: '⛳',
      friends: '👥',
      foursome: '4️⃣'
    };
    return icons[type] || '📝';
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.8) return '#4caf50'; // Green
    if (confidence >= 0.6) return '#ff9800'; // Orange
    return '#f44336'; // Red
  };

  if (isLoading) {
    return (
      <div className="request-review loading">
        <div className="loading-spinner">Loading players...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="request-review error">
        <div className="error-message">
          <h3>Error</h3>
          <p>{error}</p>
          <button onClick={loadPlayers}>Try Again</button>
          <button onClick={() => navigate('/dashboard')}>Back to Dashboard</button>
        </div>
      </div>
    );
  }

  return (
    <div className="request-review">
      <header className="review-header">
        <div className="header-content">
          <h1>Request Review & Validation</h1>
          <p>Review and approve LLM-processed player requests</p>
          
          <div className="header-actions">
            <button 
              onClick={() => navigate(`/editor/${tournamentId}`)}
              className="btn btn-primary"
            >
              Continue to Tee Sheet Editor
            </button>
          </div>
        </div>
      </header>

      <div className="review-content">
        <div className="review-sidebar">
          <div className="filters-section">
            <h3>Filters</h3>
            
            <div className="filter-group">
              <label>Status:</label>
              <select 
                value={filter} 
                onChange={(e) => setFilter(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Players</option>
                <option value="needs-review">Needs Review</option>
                <option value="processed">Processed</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Search:</label>
              <input
                type="text"
                placeholder="Player name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
          </div>

          <div className="players-list">
            <h3>Players ({filteredPlayers.length})</h3>
            {filteredPlayers.map(player => (
              <div
                key={player._id}
                className={`player-list-item ${selectedPlayer?._id === player._id ? 'selected' : ''} ${player.needsReview ? 'needs-review' : ''}`}
                onClick={() => setSelectedPlayer(player)}
              >
                <div className="player-list-name">{player.name}</div>
                <div className="player-list-indicators">
                  {player.needsReview && <span className="indicator review">⚠️</span>}
                  {player.llmProcessed && <span className="indicator processed">✅</span>}
                  {(player.timeRequests?.length || 0) + (player.partnerRequests?.length || 0) + (player.teamRequests?.length || 0) > 0 && 
                    <span className="indicator has-requests">📝</span>
                  }
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="review-main">
          {selectedPlayer ? (
            <PlayerDetailView
              player={selectedPlayer}
              onApprove={() => approveAllRequests(selectedPlayer._id)}
              onMarkReview={() => markAsNeedsReview(selectedPlayer._id)}
              onUpdate={(updates) => updatePlayerRequests(selectedPlayer._id, updates)}
              saving={saving}
              getRequestTypeIcon={getRequestTypeIcon}
              getConfidenceColor={getConfidenceColor}
            />
          ) : (
            <div className="no-selection">
              <h3>Select a player to review their requests</h3>
              <p>Choose a player from the list to see their original request and LLM interpretations.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PlayerDetailView({ 
  player, 
  onApprove, 
  onMarkReview, 
  onUpdate, 
  saving,
  getRequestTypeIcon,
  getConfidenceColor 
}) {
  const hasRequests = (player.timeRequests?.length || 0) + 
                     (player.partnerRequests?.length || 0) + 
                     (player.teamRequests?.length || 0) > 0;

  return (
    <div className="player-detail">
      <div className="player-header">
        <div className="player-info">
          <h2>{player.name}</h2>
          <div className="player-meta">
            <span className="ghin">GHIN: {player.ghin}</span>
            <span className="handicap">HI: {player.handicapIndex}</span>
            {player.llmConfidence !== undefined && (
              <span 
                className="confidence"
                style={{ color: getConfidenceColor(player.llmConfidence) }}
              >
                Confidence: {(player.llmConfidence * 100).toFixed(0)}%
              </span>
            )}
          </div>
        </div>

        <div className="player-actions">
          {player.needsReview ? (
            <button 
              onClick={onApprove}
              disabled={saving}
              className="btn btn-success"
            >
              {saving ? 'Saving...' : 'Approve All'}
            </button>
          ) : (
            <button 
              onClick={onMarkReview}
              disabled={saving}
              className="btn btn-warning"
            >
              Mark for Review
            </button>
          )}
        </div>
      </div>

      <div className="original-request">
        <h3>Original Request</h3>
        <div className="request-text">
          {player.rawPartnerRequest || <em>No request provided</em>}
        </div>
      </div>

      {!hasRequests && player.llmProcessed && (
        <div className="no-requests">
          <h3>No Requests Identified</h3>
          <p>The LLM did not identify any time, partner, or team requests from the original text.</p>
        </div>
      )}

      {player.timeRequests && player.timeRequests.length > 0 && (
        <div className="requests-section">
          <h3>Time Requests</h3>
          {player.timeRequests.map((request, index) => (
            <RequestCard
              key={index}
              request={request}
              type="time"
              icon={getRequestTypeIcon(request.type)}
              confidenceColor={getConfidenceColor(request.confidence)}
            />
          ))}
        </div>
      )}

      {player.partnerRequests && player.partnerRequests.length > 0 && (
        <div className="requests-section">
          <h3>Partner Requests</h3>
          {player.partnerRequests.map((request, index) => (
            <RequestCard
              key={index}
              request={request}
              type="partner"
              icon={getRequestTypeIcon(request.type)}
              confidenceColor={getConfidenceColor(request.confidence)}
            />
          ))}
        </div>
      )}

      {player.teamRequests && player.teamRequests.length > 0 && (
        <div className="requests-section">
          <h3>Team Requests</h3>
          {player.teamRequests.map((request, index) => (
            <RequestCard
              key={index}
              request={request}
              type="team"
              icon={getRequestTypeIcon(request.type)}
              confidenceColor={getConfidenceColor(request.confidence)}
            />
          ))}
        </div>
      )}

      {player.llmError && (
        <div className="error-section">
          <h3>Processing Error</h3>
          <div className="error-details">
            {player.llmError}
          </div>
        </div>
      )}
    </div>
  );
}

function RequestCard({ request, type, icon, confidenceColor }) {
  const renderRequestDetails = () => {
    switch (type) {
      case 'time':
        return (
          <div className="request-details">
            <div className="detail-row">
              <span className="label">Type:</span>
              <span className="value">{request.type}</span>
            </div>
            {request.value && (
              <div className="detail-row">
                <span className="label">Value:</span>
                <span className="value">{request.value}</span>
              </div>
            )}
          </div>
        );
      
      case 'partner':
        return (
          <div className="request-details">
            <div className="detail-row">
              <span className="label">Type:</span>
              <span className="value">{request.type}</span>
            </div>
            {request.targetNames && request.targetNames.length > 0 && (
              <div className="detail-row">
                <span className="label">Partners:</span>
                <span className="value">{request.targetNames.join(', ')}</span>
              </div>
            )}
          </div>
        );
      
      case 'team':
        return (
          <div className="request-details">
            <div className="detail-row">
              <span className="label">Type:</span>
              <span className="value">{request.type}</span>
            </div>
            {request.teamName && (
              <div className="detail-row">
                <span className="label">Team:</span>
                <span className="value">{request.teamName}</span>
              </div>
            )}
            {request.memberNames && request.memberNames.length > 0 && (
              <div className="detail-row">
                <span className="label">Members:</span>
                <span className="value">{request.memberNames.join(', ')}</span>
              </div>
            )}
            <div className="detail-row">
              <span className="label">Must Play Together:</span>
              <span className="value">{request.mustPlayTogether ? 'Yes' : 'No'}</span>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="request-card">
      <div className="request-header">
        <span className="request-icon">{icon}</span>
        <span className="request-type">{request.type}</span>
        <span 
          className="confidence-badge"
          style={{ backgroundColor: confidenceColor }}
        >
          {(request.confidence * 100).toFixed(0)}%
        </span>
        <span className="priority-badge priority-{request.priority}">
          P{request.priority}
        </span>
      </div>
      
      {renderRequestDetails()}
    </div>
  );
}

export default RequestReview;